import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { SkMaxPrice, Update as ApiUpdate, ValidationResult } from '~/Api/User/ProductGroup/SkMaxPrice';

import { Action, ActionType } from './Action';

export interface SkMaxPriceState {
	request: Promise<any>;
	isFetching: boolean;
	skMaxPrice: SkMaxPrice | null;
}

type UpdateState =
	| { name: 'scheduled'; timeoutId: NodeJS.Timeout; data: ApiUpdate }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	request: Promise<any>;
	isFetching: boolean;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]: SkMaxPriceState };
	update: Update | null;
	export: Export | null;
}

function createState(): State {
	return {
		byId: {},
		update: null,
		export: null,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						skMaxPrice: state.byId[action.groupId]?.skMaxPrice ?? null,
					},
				},
			};

		case ActionType.receive: {
			const skMaxPrice = state.byId[action.groupId];
			if (skMaxPrice?.request !== action.request) {
				return state;
			}

			if (!action.skMaxPrice) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...skMaxPrice,
						isFetching: false,
						skMaxPrice: action.skMaxPrice,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						data: action.update,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState && updateState.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const skMaxPriceState = byId[action.groupId];

			if (action.skMaxPrice && skMaxPriceState) {
				byId[action.groupId] = {
					...skMaxPriceState,
					skMaxPrice: action.skMaxPrice,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.skMaxPrice ? { name: 'updated' } : { name: 'failed' },
				},
				export: null,
			};
		}

		case ActionType.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					request: action.request,
					isFetching: true,
					validationResult: null,
				},
			};

		case ActionType.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectSkMaxPrice(state: State, groupId: string): SkMaxPriceState | null {
	return state.byId[groupId];
}

export function selectExport(state: State, groupId: string): Export | null {
	return state.export?.groupId === groupId ? state.export : null;
}
